/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-extra-boolean-cast */
/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  HStack,
  Stack,
  Button,
  Box,
  Flex,
  Input,
  FormControl,
  FormErrorMessage,
  useTheme as useChakraTheme,
} from '@chakra-ui/core';
import React, { FC, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IoClose, IoRadioButtonOff } from 'react-icons/io5';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  SortableContainerProps,
  SortableElementProps,
} from 'react-sortable-hoc';
import { MdCheckBoxOutlineBlank, MdDragIndicator } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from '../../../../models/hooks';
import { useGetItemProperty } from '../hooks/useGetItemProperty';
import { useUpdateFormItem } from '../hooks/useUpdateFormItem';
import { FieldOptionT } from '../types';

const MAX_OPTIONS_COUNT = 20;

export function swap<T>(array: T[], indexA: number, indexB: number): T[] {
  const newArray = [...array];
  [newArray[indexA], newArray[indexB]] = [newArray[indexB], newArray[indexA]];
  return newArray;
}

interface FormOptionsProps {
  fieldId: string;
  hideSelection?: boolean;
  type: 'radio' | 'checkbox';
}

const FormOptions: FC<FormOptionsProps> = React.memo(
  function OptionSchemaBuilderComponent({ fieldId, hideSelection, type }) {
    const language = useStoreState((state) => state.formBuilder.currentLanguage);

    const options = useGetItemProperty<FieldOptionT[]>(fieldId, 'optionList') || [];
    const handleOnChange = useUpdateFormItem();

    const handleOptionTextChange = useCallback(
      (newTextValue: string, index: number) => {
        const updatedOptions = options.map((option, i) => {
          if (i === index) {
            const newOptionText = { ...option.text, [language]: newTextValue };
            if (language === 'en' && !Boolean(option?.text?.fr?.trim())) {
              newOptionText.fr = newTextValue;
            } else if (language === 'fr' && !Boolean(option?.text?.en?.trim())) {
              newOptionText.en = newTextValue;
            }
            return { ...option, text: newOptionText };
          }
          return option;
        });
        handleOnChange(fieldId, { optionList: updatedOptions });
      },
      [options, language, handleOnChange],
    );

    const handleRemoveOption = useCallback(
      (index: number) => {
        const updatedOptions = options.filter((_, i) => i !== index);
        handleOnChange(fieldId, { optionList: updatedOptions });
      },
      [options, handleOnChange],
    );

    const handleAddOption = useCallback(() => {
      handleOnChange(fieldId, { optionList: [...options, createNewOption(options.length)] });
    }, [options, handleOnChange]);

    const handleSortEnd = useCallback(
      ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        if (oldIndex !== newIndex) {
          handleOnChange(fieldId, { optionList: swap(options, oldIndex, newIndex) });
        }
      },
      [options, handleOnChange],
    );

    return (
      <Stack align="flex-start" spacing={4} w="100%">
        <OptionList
          options={options}
          language={language}
          onSortEnd={handleSortEnd}
          onOptionChange={handleOptionTextChange}
          onRemoveOption={handleRemoveOption}
          hideSelection={hideSelection}
          useDragHandle
          type={type}
          fieldId={fieldId}
        />
        {options.length < MAX_OPTIONS_COUNT && <AddOptionButton onClick={handleAddOption} />}
      </Stack>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.hideSelection === nextProps.hideSelection && prevProps.type === nextProps.type;
  },
);

const createNewOption = (currentIndex: number): FieldOptionT => ({
  id: uuidv4(),
  text: { en: '', fr: '' },
  key: String.fromCharCode(97 + (currentIndex % 26)),
});

interface OptionListProps extends SortableContainerProps {
  options: FieldOptionT[];
  language: string;
  onOptionChange: (value: string, index: number) => void;
  onRemoveOption: (index: number) => void;
  hideSelection?: boolean;
  type: 'radio' | 'checkbox';
  fieldId: string;
}

const OptionList = SortableContainer<OptionListProps>(
  ({ options, onOptionChange, language, onRemoveOption, hideSelection, type, fieldId }: OptionListProps) => (
    <Stack spacing={0} w="100%">
      {options.map((option, index) => {
        const startWithEditView = options.length - 1 === index && !option.text[language]?.trim();
        return (
          <SortableOption
            key={`option-${option.id}`}
            option={option}
            index={index}
            optionIndex={index}
            language={language}
            onOptionChange={onOptionChange}
            onRemoveOption={onRemoveOption}
            hideSelection={hideSelection}
            type={type}
            optionsCount={options.length}
            startWithEditView={startWithEditView}
            fieldId={fieldId}
          />
        );
      })}
    </Stack>
  ),
);

interface SortableOptionProps extends SortableElementProps {
  option: FieldOptionT;
  optionIndex: number;
  language: string;
  onOptionChange: (value: string, index: number) => void;
  onRemoveOption: (index: number) => void;
  hideSelection?: boolean;
  type: 'radio' | 'checkbox';
  optionsCount: number;
  startWithEditView?: boolean;
  fieldId: string;
}

const DragHandle = SortableHandle(() => (
  <Flex alignItems="center" justifyContent="center" h="100%" px={1} cursor="grab">
    <MdDragIndicator color={useChakraTheme().colors.gray[400]} size="20px" />
  </Flex>
));

const SortableOption = React.memo(
  SortableElement<SortableOptionProps>(function SortableOptionComponent({
    option,
    optionIndex,
    language,
    onOptionChange: parentOnOptionChange,
    onRemoveOption,
    hideSelection,
    type,
    optionsCount,
    startWithEditView,
    fieldId,
  }: SortableOptionProps) {
    const { t } = useTranslation('hrFormTemplate');
    const chakraTheme = useChakraTheme();

    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState(option.text[language] || '');
    const inputRef = useRef<HTMLInputElement>(null);

    const innerPath = `${optionIndex}.text.${language}`;
    const error = useStoreState((state) => state.formBuilder.errorsByTarget(fieldId, innerPath));
    const removeError = useStoreActions((state) => state.formBuilder.removeError);
    const previousErrorRef = useRef<string | undefined>();

    const currentError = useMemo(() => {
      if (!error) {
        if (previousErrorRef.current !== undefined) previousErrorRef.current = undefined;
        return undefined;
      }

      if (isEqual(previousErrorRef.current, error)) {
        return previousErrorRef.current;
      }
      previousErrorRef.current = error;
      return error;
    }, [error]);

    const hasError = useMemo(() => !!currentError, [currentError]);

    useEffect(() => {
      setInputValue(option.text[language] || '');
    }, [option.text, language, option.text[language]]);

    useEffect(() => {
      if (inputRef.current && startWithEditView) {
        inputRef.current.focus();
      }
    }, [startWithEditView]);

    const commitChange = useCallback(() => {
      if (inputValue !== (option.text[language] || '')) {
        parentOnOptionChange(inputValue, optionIndex);
      }
    }, [inputValue, option.text, language, parentOnOptionChange, optionIndex]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
          if (isFocused) {
            commitChange();
            setIsFocused(false);
          }
        }
      };
      if (isFocused) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isFocused, commitChange]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        removeError({ targetId: fieldId, innerPath });
      },
      [innerPath, fieldId],
    );

    const handleInputFocus = useCallback(() => setIsFocused(true), []);

    const handleInputBlur = useCallback(() => {
      setTimeout(() => {
        if (document.activeElement !== inputRef.current && isFocused) {
          commitChange();
          setIsFocused(false);
        }
        //  else if (!isFocused && document.activeElement !== inputRef.current) {
        // }
        else if (isFocused) {
          commitChange();
          setIsFocused(false);
        }
      }, 0);
    }, [commitChange, isFocused]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitChange();
          inputRef.current?.blur();
        } else if (e.key === 'Escape') {
          setInputValue(option.text[language] || '');
          inputRef.current?.blur();
        }
      },
      [commitChange, option.text, language],
    );

    const handleRemove = useCallback(() => {
      onRemoveOption(optionIndex);
      removeError({ targetId: fieldId, innerPath });
    }, [onRemoveOption, optionIndex, innerPath, fieldId]);

    return (
      <HStack
        w="100%"
        // bg={isFocused ? chakraTheme.colors.gray[50] : chakraTheme.colors.white}
        sx={{ '&.is-dragging-option-item': { opacity: 0.5, boxShadow: 'lg', bg: chakraTheme.colors.blue[50] } }}
        alignItems="center"
        spacing={2}
        py={1.5}
        borderWidth="1px"
        borderColor={isFocused ? chakraTheme.colors.blue[500] : 'transparent'}
        borderRadius="md"
      >
        <DragHandle />
        {!hideSelection && (
          <Box>
            {type === 'radio' ? (
              <IoRadioButtonOff size="1.1em" color={chakraTheme.colors.gray[400]} />
            ) : (
              <MdCheckBoxOutlineBlank size="1.1em" color={chakraTheme.colors.gray[400]} />
            )}
          </Box>
        )}
        <FormControl isInvalid={hasError} flex={1} my={0} py={0}>
          <Input
            ref={inputRef}
            variant="unstyled"
            pr={2}
            flex={1}
            size="sm"
            fontSize="sm"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder={t('formBuilder.optionInputPlaceholder', { index: optionIndex + 1 }) || `Option ${optionIndex + 1}`}
            _placeholder={{ color: chakraTheme.colors.gray[400] }}
            borderColor={hasError ? chakraTheme.colors.red[500] : 'transparent'}
            boxShadow={hasError ? `0 0 0 1px ${chakraTheme.colors.red[500]}` : 'none'}
          />
          {hasError && currentError && (
            <FormErrorMessage marginTop={0} fontSize="xs">
              {t(currentError)}
            </FormErrorMessage>
          )}
        </FormControl>
        {optionsCount > 1 && (
          <Button
            aria-label={t('formBuilder.removeOptionAriaLabel', { index: optionIndex + 1 }) || `Remove option ${optionIndex + 1}`}
            onClick={handleRemove}
            variant="ghost"
            colorScheme="red"
            size="xs"
            minW="auto"
            px={0.5}
            sx={{
              aspectRatio: '1/1',
            }}
          >
            <IoClose />
          </Button>
        )}
      </HStack>
    );
  }),
);

interface AddOptionButtonProps {
  onClick: () => void;
}

const AddOptionButton: FC<AddOptionButtonProps> = React.memo(({ onClick }) => {
  const { t } = useTranslation('hrFormTemplate');
  return (
    <Button variant="link" fontSize="sm" fontWeight="normal" onClick={onClick} colorScheme="blue" mt={1}>
      {t('formBuilder.addOption') || '+ Add Option'}
    </Button>
  );
});

AddOptionButton.displayName = 'AddOptionButton';

FormOptions.displayName = 'FormOptions';
export default FormOptions;
